import Button from '@rio-cloud/rio-uikit/Button';
import SortDirectionEnum from '@rio-cloud/rio-uikit/SortDirection';
import { useEffect, useState } from 'react';
import { IBudgetsListResponse } from '../../../api/fetchBudget';
import { BudgetRecipientEnum, IBudgetFormData } from '../createAndEdit/types';
import { OccurrenceStepEnum } from '../../../enums/occurrenceStep';
import { Budget } from '../types';
import { SortDirection, SortField } from '../utils/sortUtils';
import BudgetsTable from './components/BudgetsTable';
import Pagination from './components/Pagination';
import { useListNavigation } from './hooks/useListNavigation';

interface IBudgetsListProps {
  currentStep: OccurrenceStepEnum;
  budgets: Budget[];
  occurrenceId: string;
  budgetsListResponse?: IBudgetsListResponse | null;
  userProfile?: BudgetRecipientEnum | string;
  onNavigateToCreate: () => void;
  onNavigateToView: (budgetId: string, data: IBudgetFormData) => void;
  onNavigateToEdit: (budgetId: string, data: IBudgetFormData) => void;
  onLoadBudgets?: (
    sortBy?: string,
    sortDirection?: 'asc' | 'desc',
    page?: number,
    pageSize?: number,
  ) => Promise<void>;
}

const mapSortFieldToApi = (field: SortField | null): string | undefined => {
  if (!field) return undefined;
  const fieldMap: Record<SortField, string> = {
    consultantName: 'consultant',
    origin: 'origin',
    recipient: 'recipient',
    user: 'username',
    version: 'lastVersionNumber',
    date: 'createdAt',
    status: 'status',
    profile: 'profile',
    versionDate: 'versionDate',
  };
  return fieldMap[field];
};

const BudgetsList = (props: IBudgetsListProps) => {
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirectionEnum.ASCENDING);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = props.budgetsListResponse?.pageSize || 50;

  const { handleCreateBudget, handleViewBudget, handleEditBudget, handleViewOrEditBudget } = useListNavigation({
    occurrenceId: props.occurrenceId,
    budgetsListResponse: props.budgetsListResponse,
    userProfile: props.userProfile as BudgetRecipientEnum | undefined,
    onNavigateToCreate: props.onNavigateToCreate,
    onNavigateToView: props.onNavigateToView,
    onNavigateToEdit: props.onNavigateToEdit,
  });

  useEffect(() => {
    if (props.budgetsListResponse) {
      setCurrentPage(props.budgetsListResponse.currentPage);
    }
  }, [props.budgetsListResponse]);

  const handleSort = async (field: SortField) => {
    const newSortDirection =
      sortField === field && sortDirection === SortDirectionEnum.ASCENDING
        ? SortDirectionEnum.DESCENDING
        : SortDirectionEnum.ASCENDING;

    setSortField(field);
    setSortDirection(newSortDirection);

    if (props.onLoadBudgets) {
      const apiSortBy = mapSortFieldToApi(field);
      const apiSortDirection = newSortDirection === SortDirectionEnum.ASCENDING ? 'asc' : 'desc';
      await props.onLoadBudgets(apiSortBy, apiSortDirection, currentPage, itemsPerPage);
    }
  };

  const handlePageChange = async (page: number) => {
    setCurrentPage(page);
    if (props.onLoadBudgets) {
      const apiSortBy = mapSortFieldToApi(sortField);
      const apiSortDirection = sortDirection === SortDirectionEnum.ASCENDING ? 'asc' : 'desc';
      await props.onLoadBudgets(apiSortBy, apiSortDirection, page, itemsPerPage);
    }
  };

  return (
    <div className="w-full">
      <div className="text-size-h4 font-bold mb-2">Orçamentos</div>
      <div className="text-color-status-resting text-size-14 mb-4">
        Aqui estão todos os orçamentos atribuídos a esse chamado
      </div>

      {props.userProfile === BudgetRecipientEnum.CONSULTANT && props.currentStep === OccurrenceStepEnum.THIRD_DIAGNOSIS && (
        <div className="mb-8">
          <Button className="btn btn-primary px-20" onClick={handleCreateBudget}>
            Criar orçamento
          </Button>
        </div>
      )}

      <BudgetsTable
        budgets={props.budgets}
        occurrenceId={props.occurrenceId}
        onViewBudget={handleViewBudget}
        onEditBudget={handleEditBudget}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
        userProfile={props.userProfile}
      />

      <Pagination
        currentPage={currentPage}
        totalItems={props.budgetsListResponse?.totalElements || 0}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

export default BudgetsList;
